import { useRouter } from 'next/router';
import React, { useContext, useEffect, useState } from 'react';
import { CategoryProducts } from 'src/components/shared/category/CategoryProducts';
import { CategorySubTitle } from 'src/components/shared/category/CategoryTitle';
import { Loading } from 'src/components/shared/elements/loading';
import { Paginator } from 'src/components/shared/elements/paginator';
import { ProductSortSelector } from 'src/components/shared/products/ProductSortSelector';
import { KaimonoWebRoutes } from 'src/config/routes';
import { usePaginator } from 'src/hooks/usePaginator';
import { useProducts } from 'src/hooks/useProducts';
import { useShopSelector } from 'src/hooks/useShopSelector';
import { Type as UseProductsFeatureTagType } from 'src/libs/api/productsApi';
import type { MiddleCategorySchema, SmallCategorySchema, TinyCategorySchema } from 'src/models/category/schema';
import { BrandsContext } from 'src/stores/Brands';
import { CategoryContext } from 'src/stores/Category';
import { I18nContext } from 'src/stores/I18n';
import { FilteredBrands } from '../FilteredBrands';

type Props = {
  slug: string;
  activeCategorySlug?: string;
  defaultBrandSlug?: string;
  categories: MiddleCategorySchema[] | SmallCategorySchema[] | TinyCategorySchema[];
  categoryType: 'middleCategory' | 'smallCategory' | 'tinyCategory';
  isAll?: boolean;
};

export const CategoryComponentWithSlug: React.FC<Props> = ({
  slug,
  activeCategorySlug,
  defaultBrandSlug,
  categories,
  categoryType,
  isAll
}) => {
  const router = useRouter();
  const localeResources = useContext(I18nContext);
  const categoryForCheck = useContext(CategoryContext);
  const brands = useContext(BrandsContext);
  const brandSlug = router.query.brand as string;
  const brandId = brands.find((brand) => brand.slug === brandSlug)?.brandId;
  const [activeBrandId, setActiveBrandId] = useState<number>(brandId ?? 0);
  const drugCategory = categoryForCheck.largeCategories.find((_) => _.slug === 'drug');
  const middleCategory = categoryForCheck.middleCategories;
  const smallCategory = categoryForCheck.smallCategories;
  const [category, setCategory] = useState<MiddleCategorySchema | SmallCategorySchema | TinyCategorySchema>();
  const [subTitle, setSubTitle] = useState<string>('');
  const [buttonPath, setButtonPath] = useState<string>();
  const { currentShop } = useShopSelector();
  const initialPageIndex = parseInt(router.query.page as string, 10) || 1;
  const { pageIndex, setPageIndex, pageIndexClickHandler, routeLoading } = usePaginator();

  const conditionalPageIndex = activeBrandId ? 1 : pageIndex;
  let idValue;
  let type: 'middleCategoryId' | 'smallCategoryId' | 'tinyCategoryId' = 'middleCategoryId';

  if (categoryType === 'middleCategory') {
    idValue = (categories as MiddleCategorySchema[]).find((category) => category.slug === slug)?.middleCategoryId;
    type = UseProductsFeatureTagType.MIDDLE_CATEGORY_ID;
  } else if (categoryType === 'smallCategory') {
    idValue = (categories as SmallCategorySchema[]).find((category) => category.slug === slug)?.smallCategoryId;
    type = UseProductsFeatureTagType.SMALL_CATEGORY_ID;
  } else if (categoryType === 'tinyCategory') {
    idValue = (categories as TinyCategorySchema[]).find((category) => category.slug === slug)?.tinyCategoryId;
    type = UseProductsFeatureTagType.TINY_CATEGORY_ID;
  }
  const id = idValue ?? 0;
  const initialDisplayCount = isAll ? 20 : 8;
  const additionalParam = activeBrandId
    ? {
        type: 'brandId',
        id: activeBrandId
      }
    : undefined;
  const isSort = true;

  const { products, isProductsLoading, totalPageCount, sort, setSort } = useProducts(
    id,
    type as UseProductsFeatureTagType,
    conditionalPageIndex,
    initialDisplayCount,
    currentShop?.id,
    additionalParam,
    isSort
  );

  const isMedicine = (id: number, ids?: number[]) => {
    if (!ids) return false;
    return ids.includes(id);
  };

  useEffect(() => {
    if (categoryType === 'middleCategory') {
      const middleCategories = categories as MiddleCategorySchema[];
      const middleCategory = middleCategories.find((_) => _.slug === slug) as MiddleCategorySchema;
      setCategory(middleCategory);
      setSubTitle(
        isMedicine(middleCategory.middleCategoryId, drugCategory?.middleCategoryIds)
          ? middleCategory.name
          : localeResources.commons.mustBuy + middleCategory.name
      );
      setButtonPath(KaimonoWebRoutes.middleCategories.show(middleCategory.slug));
    } else if (categoryType === 'smallCategory') {
      const smallCategories = categories as SmallCategorySchema[];
      const smallCategory = smallCategories.find((_) => _.slug === slug) as SmallCategorySchema;
      const middleCategoryIdForDrug = middleCategory.find((_) =>
        _.smallCategoryIds.includes(smallCategory.smallCategoryId)
      )?.middleCategoryId;
      setCategory(smallCategory);
      setSubTitle(
        isMedicine(middleCategoryIdForDrug ?? 0, drugCategory?.middleCategoryIds)
          ? smallCategory.name
          : localeResources.commons.mustBuy + smallCategory.name
      );
      setButtonPath(KaimonoWebRoutes.smallCategories.show(smallCategory.slug));
    } else {
      const tinyCategories = categories as TinyCategorySchema[];
      const tinyCategory = tinyCategories.find((tinyCategory) => tinyCategory.slug === slug) as TinyCategorySchema;
      const smallCategoryIdForDrug = smallCategory.find((smallCategory) =>
        smallCategory.tinyCategoryIds.includes(tinyCategory.tinyCategoryId)
      )?.smallCategoryId;
      setCategory(tinyCategory);
      setSubTitle(
        isMedicine(smallCategoryIdForDrug ?? 0, drugCategory?.middleCategoryIds)
          ? tinyCategory.name
          : localeResources.commons.mustBuy + tinyCategory.name
      );
      setButtonPath(KaimonoWebRoutes.tinyCategories.show(tinyCategory.slug));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    setPageIndex(initialPageIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPageIndex]);

  if (!category || isProductsLoading || routeLoading) {
    return <Loading />;
  }

  return (
    <>
      {!isAll && <CategorySubTitle text={subTitle} />}
      <div className="flex flex-col gap-3 py-4">
        {categoryType !== 'middleCategory' && (
          <ProductSortSelector sort={sort} setSort={setSort} setPageIndex={setPageIndex} />
        )}
        {/* small_categoryとtiny_categoryにのみブランド絞り込みが表示される */}
        {activeCategorySlug && (
          <FilteredBrands
            activeBrandId={activeBrandId}
            setActiveBrandId={setActiveBrandId}
            categorySlug={activeCategorySlug}
            defaultBrandSlug={defaultBrandSlug}
          />
        )}
      </div>
      {products && (
        <>
          <CategoryProducts products={products} moreButtonPath={isAll ? undefined : buttonPath} />
          {isAll && (
            <Paginator
              total={totalPageCount ?? 0}
              current={conditionalPageIndex}
              pageIndexClickHandler={pageIndexClickHandler}
            />
          )}
        </>
      )}
    </>
  );
};
